import { __decorate } from "tslib";
import { ChameleonElementMixin } from '../common/mixin';
import { BaseCalendar } from './BaseCalendar';
import { attr } from '@microsoft/fast-element';
export class CalendarComponent extends ChameleonElementMixin(BaseCalendar) {
    constructor() {
        super(...arguments);
        this.selectedDates = '';
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.isConnected && this.selectedDates) {
            const initialDate = this.selectedDatesToObject(this.selectedDates);
            this.selectDate(initialDate);
        }
    }
    selectedDatesChanged(_oldValue, newValue) {
        this.selectedDates = newValue;
    }
    goToPreviousMonthListener(dayDiff) {
        const previousMonth = this.getMonthInfo(this.month).previous;
        if (this.year !== previousMonth.year) {
            this.year = previousMonth.year;
        }
        this.month = previousMonth.month;
        const dayToFocus = dayDiff ? previousMonth.length + dayDiff : previousMonth.length;
        this.focusDate({ day: dayToFocus, month: this.month, year: this.year });
    }
    goToNextMonthListener(dayDiff) {
        const nextMonth = this.getMonthInfo(this.month).next;
        if (this.year !== nextMonth.year) {
            this.year = nextMonth.year;
        }
        this.month = nextMonth.month;
        const dayToFocus = dayDiff ? dayDiff : 1;
        this.focusDate({ day: dayToFocus, month: this.month, year: this.year });
    }
    todayButtonListener() {
        const date = new Date();
        const today = {
            day: date.getDate(),
            month: date.getMonth() + 1,
            year: date.getFullYear(),
        };
        this.month = today.month;
        this.year = today.year;
        this.selectDate(today);
    }
    handleChangeListener(event) {
        // We need to stop propagation here or the onChange for the DatePicker will trigger twice
        event.stopPropagation();
    }
    handleClickListener(event) {
        const date = event === null || event === void 0 ? void 0 : event.detail;
        if (date) {
            const { day, month, year } = date;
            this.selectDate({ day, month, year });
        }
    }
    handleCalendarKeydownListener(event) {
        if (event.key === 'Escape') {
            // We want to propagate the escape key for closing the popover
            return false;
        }
        // This handler should trigger only when a key is press on calendar-container target
        event.stopPropagation();
        if (event.key.includes('Arrow')) {
            const date = new Date();
            const today = {
                day: date.getDate(),
                month: date.getMonth() + 1,
                year: date.getFullYear(),
            };
            if (this.selectedDates) {
                const { day, month, year } = this.selectedDatesToObject(this.selectedDates);
                this.focusDate({ day, month, year });
            }
            else if (today.month === this.month) {
                this.focusDate({ day: today.day, month: today.month, year: today.year });
            }
            else {
                this.focusDate({ day: 1, month: this.month, year: this.year });
            }
        }
        return true;
    }
    /**
     * Handle keydown only on calendar cells
     * @param event KeyboarEvent
     * @param date information on the date targeted
     * @returns if event is handled or not
     */
    handleKeydown(event, date) {
        if (event.key === 'Escape') {
            // We want to propagate the escape key for closing the popover
            return false;
        }
        event.stopPropagation();
        const { key } = event;
        const { day, month, year } = date;
        let dateToFocus = { day, month, year };
        if (key.includes('Arrow')) {
            this.unfocusDate(date);
            const dayModifiers = {
                ArrowRight: 1,
                ArrowLeft: -1,
                ArrowDown: 7,
                ArrowUp: -7,
            };
            const dayToFocus = day + dayModifiers[key];
            if (dayToFocus <= 0) {
                const dayDiff = dayToFocus;
                this.goToPreviousMonthListener(dayDiff);
                return true;
            }
            const lastDayOfMonth = this.getMonthInfo(this.month).length;
            if (dayToFocus > lastDayOfMonth) {
                const dayDiff = dayToFocus - lastDayOfMonth;
                this.goToNextMonthListener(dayDiff);
                return true;
            }
            dateToFocus = {
                day: dayToFocus,
                month: month,
                year: year,
            };
            this.focusDate(dateToFocus);
        }
        if (key === 'Enter') {
            this.selectDate(dateToFocus);
            return true;
        }
        if (key === 'Tab') {
            this.unfocusDate(dateToFocus);
            return true;
        }
        return false;
    }
    selectDate({ day, month, year }) {
        this.selectedDates = `${month}-${day}-${year}`;
        this.month = month;
        this.year = year;
        this.focusDate({ day, month, year });
        this.emitChangeEvent({ date: { day, month, year }, selectedDates: this.selectedDates });
    }
    focusDate(date) {
        requestAnimationFrame(() => {
            const cellElement = this.findCellElement(date);
            cellElement === null || cellElement === void 0 ? void 0 : cellElement.focus();
            cellElement === null || cellElement === void 0 ? void 0 : cellElement.setAttribute('tabindex', '0');
        });
    }
    unfocusDate(date) {
        requestAnimationFrame(() => {
            const cellElement = this.findCellElement(date);
            cellElement === null || cellElement === void 0 ? void 0 : cellElement.blur();
            cellElement === null || cellElement === void 0 ? void 0 : cellElement.setAttribute('tabindex', '-1');
        });
    }
    emitChangeEvent(emitProps) {
        this.$emit('change', emitProps, { bubbles: false });
    }
    findCellElement({ day, month, year }) {
        var _a;
        const dayCells = Array.from((_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelectorAll('[part="day"]'));
        const cellElement = dayCells === null || dayCells === void 0 ? void 0 : dayCells.find((d) => {
            var _a;
            const dateQuery = (_a = d.querySelector('slot')) === null || _a === void 0 ? void 0 : _a.name;
            const dateToFocus = `${month}-${day}-${year}`;
            return dateQuery === dateToFocus;
        });
        return cellElement;
    }
    selectedDatesToObject(selectedDates) {
        const dateParts = selectedDates.split('-').map((part) => part.trim());
        const month = parseInt(dateParts[0]);
        const day = parseInt(dateParts[1]);
        const year = parseInt(dateParts[2]);
        return { day, month, year };
    }
}
__decorate([
    attr({ attribute: 'selected-dates' })
], CalendarComponent.prototype, "selectedDates", void 0);
